import { Link, useParams } from 'react-router-dom';
import {
  ARCANA_LABELS,
  BRAND,
  CARDS,
  SUIT_LABELS,
  cardSeo,
  cardSlug,
  findCardBySlug,
  findMajorMeaning,
} from '@tarot/shared';
import { TarotCard } from '../../components/TarotCard';
import { useTrack } from '../../lib/track';
import { useDocumentMeta } from '../../lib/meta';

/**
 * 카드 1장 상세 (/cards/:slug) — 검색 유입의 착지 페이지.
 * "타로 바보 카드 의미" 같은 검색으로 들어온 사람에게 의미를 보여주고,
 * 하단 CTA로 실제 타로 보기로 유도한다.
 */
export function CardDetailPage() {
  useTrack('view:card');
  const { slug = '' } = useParams();
  const card = findCardBySlug(slug);
  const meaning = card ? findMajorMeaning(card.id) : undefined;

  // 제목·설명·canonical은 shared의 cardSeo 단일 출처 — 빌드 시 생성하는 정적 HTML과 같은 값이어야
  // 크롤러가 본 문서와 SPA 이동 후의 문서가 어긋나지 않는다 (packages/shared/src/seo.ts)
  const seo = card ? cardSeo(card) : undefined;
  useDocumentMeta({
    title: seo?.title ?? BRAND.title,
    description: seo?.description ?? BRAND.description,
    canonical: seo ? `${BRAND.siteUrl}${seo.path}` : undefined,
  });

  if (!card) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <p className="text-lg">그런 카드를 찾을 수 없어요 🕯️</p>
        <Link
          to="/cards"
          className="flex h-12 w-full max-w-xs items-center justify-center rounded-2xl bg-gold px-5 text-sm font-semibold text-night"
        >
          카드 도감으로
        </Link>
      </div>
    );
  }

  const prev = CARDS[card.id - 1];
  const next = CARDS[card.id + 1];

  return (
    <article className="flex flex-col gap-6">
      <nav className="text-xs text-moon-dim">
        <Link to="/cards" className="hover:text-moon">
          카드 도감
        </Link>
        <span className="mx-1.5 opacity-50">›</span>
        <span>{card.nameKo}</span>
      </nav>

      <header className="flex flex-col items-center gap-3 text-center">
        <div className="w-40">
          <TarotCard drawn={{ cardId: card.id, reversed: false, position: 0 }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{card.nameKo}</h1>
          <p className="mt-0.5 text-sm text-moon-dim">{card.nameEn}</p>
        </div>
        {/* 용어를 처음 보는 사람을 위해 사전 항목으로 바로 연결 */}
        <p className="flex flex-wrap items-center justify-center gap-1.5 text-xs text-moon-dim">
          <Chip to="/glossary#arcana">{ARCANA_LABELS[card.arcana]}</Chip>
          {card.arcana === 'major' ? (
            <Chip>{card.id}번</Chip>
          ) : (
            card.suit && <Chip to="/glossary#suit">{SUIT_LABELS[card.suit].ko}</Chip>
          )}
        </p>
      </header>

      {/* 메이저 22장은 카드별 해설(shared MAJOR_MEANINGS), 마이너는 키워드 기반 안내 */}
      {meaning && (
        <section className="flex flex-col gap-2">
          <h2 className="text-lg font-bold">이 카드가 담은 이야기</h2>
          <p className="text-sm leading-relaxed text-moon-dim">{meaning.story}</p>
        </section>
      )}

      <Meaning
        title="정방향 의미"
        keywords={card.keywordsUp}
        body={
          meaning?.upright ??
          `${card.nameKo} 카드가 정방향으로 나오면 ${card.keywordsUp.join(', ')}의 기운을 말해요. ${
            card.suit
              ? `${SUIT_LABELS[card.suit].ko}는 ${SUIT_LABELS[card.suit].theme}를 다루는 슈트라, 일상에서 마주하는 구체적인 장면으로 읽는 편이에요.`
              : ''
          }`
        }
      />
      <Meaning
        title="역방향 의미"
        keywords={card.keywordsRev}
        body={
          meaning?.reversed ??
          `역방향으로 나오면 ${card.keywordsRev.join(', ')} 쪽으로 기울어요. 다만 역방향이 곧 나쁜 결과라는 뜻은 아니에요. 같은 에너지가 아직 드러나지 않았거나, 안쪽으로 향하고 있다는 신호로 읽는 경우도 많아요.`
        }
      />

      {meaning && (
        <section className="flex flex-col gap-2">
          <h2 className="text-lg font-bold">상황별로 읽으면</h2>
          <dl className="flex flex-col gap-2">
            {[
              ['연애에서', meaning.inLove],
              ['일·학업에서', meaning.inWork],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5">
                <dt className="text-xs font-semibold text-gold">{k}</dt>
                <dd className="mt-0.5 text-sm leading-relaxed text-moon-dim">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-1 rounded-xl border border-gold/25 bg-gold/5 px-3.5 py-2.5 text-sm leading-relaxed">
            <b className="text-gold">한 마디 — </b>
            {meaning.advice}
          </p>
        </section>
      )}

      <section className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/5 p-4">
        <h2 className="text-sm font-semibold text-gold">해석은 자리와 맥락에 따라 달라져요</h2>
        <p className="text-sm leading-relaxed text-moon-dim">
          같은 카드라도{' '}
          <Link to="/glossary#position" className="text-moon underline underline-offset-2">
            어느 자리
          </Link>
          에 놓였는지, 어떤 고민을 품고 뽑았는지에 따라 의미가 달라집니다. {BRAND.nameKo}는 카드 한
          장의 사전적 의미를 나열하는 대신, 뽑은 카드 전체의 조합과 자리를 함께 읽어 해석해 드려요.
        </p>
        <Link
          to="/glossary"
          className="mt-1 text-xs text-gold underline underline-offset-2 hover:text-gold-deep"
        >
          타로 용어가 낯설다면 → 용어 사전
        </Link>
      </section>

      <div className="flex flex-col gap-2">
        <Link
          to="/solo"
          className="flex h-12 w-full items-center justify-center rounded-2xl bg-gold px-5 text-sm font-semibold text-night transition hover:bg-gold-deep"
        >
          이 카드가 나올까? 타로 보러 가기
        </Link>
        <div className="flex gap-2 text-xs">
          {prev && (
            <Link
              to={`/cards/${cardSlug(prev)}`}
              className="flex-1 truncate rounded-xl border border-white/10 px-3 py-2.5 text-moon-dim transition hover:border-gold/40"
            >
              ‹ {prev.nameKo}
            </Link>
          )}
          {next && (
            <Link
              to={`/cards/${cardSlug(next)}`}
              className="flex-1 truncate rounded-xl border border-white/10 px-3 py-2.5 text-right text-moon-dim transition hover:border-gold/40"
            >
              {next.nameKo} ›
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

function Chip({ children, to }: { children: React.ReactNode; to?: string }) {
  const cls = 'rounded-full border border-gold/30 bg-gold/10 px-2.5 py-1 text-gold';
  return to ? (
    <Link to={to} className={`${cls} transition hover:border-gold/70`}>
      {children}
    </Link>
  ) : (
    <span className={cls}>{children}</span>
  );
}

function Meaning({ title, keywords, body }: { title: string; keywords: string[]; body: string }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-bold">{title}</h2>
      <ul className="flex flex-wrap gap-1.5">
        {keywords.map((k) => (
          <li key={k} className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm">
            {k}
          </li>
        ))}
      </ul>
      <p className="text-sm leading-relaxed text-moon-dim">{body}</p>
    </section>
  );
}
